import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import api, { formatEUR } from "../../lib/api";
import { toast } from "sonner";
import { Send, RefreshCw, Receipt } from "lucide-react";

const STATUS_CLS = {
  pending:               "bg-amber-50 text-amber-700 border-amber-200",
  refund_pending:        "bg-amber-50 text-amber-700 border-amber-200",
  dispatched_to_xgate:   "bg-sky-50 text-sky-700 border-sky-200",
  completed:             "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:                "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminRefunds() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/refunds")
      .then((r) => setRefunds(r.data.refunds || []))
      .catch(() => toast.error("Failed to load refunds"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const dispatchXgate = async (r) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const resp = await api.post(`/admin/refunds/${r.id}/dispatch-xgate`);
      if (resp.data?.status === "pending_second_approval") {
        toast.success("Dispatch proposed — awaiting second approver", {
          description: `Proposal ID: ${resp.data.proposal_id?.slice(0, 8)}…. A different role-permitted staff member must approve before this refund leaves our books.`,
          duration: 8000,
        });
      } else if (resp.data?.ticket_id) {
        toast.success(`Dispatched (ticket ${resp.data.ticket_id})`);
      }
      load();
    } catch (e) {
      const det = e?.response?.data?.detail || "";
      if (typeof det === "string" && det.startsWith("maker_checker_violation")) {
        toast.error("You proposed this dispatch — a different reviewer is required.");
      } else {
        toast.error(det || "Dispatch failed");
      }
    }
    finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  return (
    <AdminLayout
      title="Refunds"
      subtitle="Manually-issued refunds. Dispatch to XGate (stub) marks the refund as routed to their SEPA withdrawal API."
      actions={
        <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5" data-testid="refunds-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </Button>
      }
    >
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : refunds.length === 0 ? (
        <div className="xch-card p-12 text-center" data-testid="refunds-empty">
          <Receipt className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <div className="text-sm text-muted-foreground">No refunds yet.</div>
        </div>
      ) : (
        <div className="xch-card overflow-hidden" data-testid="refunds-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b border-border text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  <th className="px-4 py-2.5 text-left">Refund</th>
                  <th className="px-4 py-2.5 text-left">Order</th>
                  <th className="px-4 py-2.5 text-right">Amount</th>
                  <th className="px-4 py-2.5 text-left">Reason</th>
                  <th className="px-4 py-2.5 text-left">Status</th>
                  <th className="px-4 py-2.5 text-left">XGate ticket</th>
                  <th className="px-4 py-2.5 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((r) => (
                  <tr key={r.id} className="border-b border-border hover:bg-muted/20" data-testid={`refund-${r.id}`}>
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{r.id.slice(0, 12)}…</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-foreground">{(r.order_id || "").slice(0, 12)}…</td>
                    <td className="px-4 py-3 text-right tabular-nums font-mono font-semibold">{formatEUR(r.amount_eur)}</td>
                    <td className="px-4 py-3 text-xs text-foreground max-w-xs truncate">{r.reason || "—"}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`text-[10px] uppercase font-bold tracking-wider ${STATUS_CLS[r.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                        {(r.status || "pending").replace(/_/g, " ")}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">{r.xgate_ticket_id || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      {r.status !== "dispatched_to_xgate" && r.status !== "completed" ? (
                        <Button onClick={() => dispatchXgate(r)} disabled={!!busy[r.id]} size="sm"
                                className="bg-primary hover:bg-primary/90 text-white rounded-lg gap-1.5"
                                data-testid={`dispatch-${r.id}`}>
                          <Send className="w-3 h-3" /> Dispatch to XGate
                        </Button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-5 xch-card p-4 bg-amber-50/60 border-amber-200" data-testid="refund-stub-info">
        <div className="text-xs font-semibold text-amber-900 mb-1">[STUB — CONFIRM-XGATE]</div>
        <div className="text-xs text-amber-900/80">
          "Dispatch to XGate" currently records a stub ticket ID and marks the refund as routed. Real implementation will
          POST to XGate's SEPA Secure withdrawal endpoint and capture the live ticket ID; the resulting withdrawal status
          will arrive on the deposit callback URL with <code>type: withdraw</code>.
        </div>
      </div>
    </AdminLayout>
  );
}
